import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Settings, 
  Building2, 
  Palette, 
  Globe,
  AlertCircle,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { organization, updateOrganization, isAdmin } = useAuth();
  const [formData, setFormData] = useState({
    name: organization?.name || '',
    domain: organization?.domain || '',
    primary_color: organization?.primary_color || '#1d4ed8',
    logo_url: organization?.logo_url || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateOrganization(formData);
      toast.success('Settings saved successfully');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6" data-testid="settings-page">
        <Card className="border-zinc-200 bg-white">
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-zinc-950 mb-2">Admin Access Required</h2>
            <p className="text-zinc-500">Only administrators can change organization settings.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="settings-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight text-zinc-950">Settings</h1>
        <p className="text-zinc-500 mt-1">Manage your organization's branding and settings</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Organization Info */}
        <Card className="border-zinc-200 bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-zinc-950 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Organization Details
            </CardTitle>
            <CardDescription>Basic information about your organization</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-zinc-950 font-medium">Organization Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Acme Corporation"
                className="border-zinc-200 max-w-md"
                data-testid="org-name-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* White-label Settings */}
        <Card className="border-zinc-200 bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-zinc-950 flex items-center gap-2">
              <Globe className="w-5 h-5" />
              White-label Settings
            </CardTitle>
            <CardDescription>Customize branding for your organization</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="domain" className="text-zinc-950 font-medium">Custom Domain</Label>
              <Input
                id="domain"
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                placeholder="dashboard.yourcompany.com"
                className="border-zinc-200 max-w-md"
                data-testid="custom-domain-input"
              />
              <p className="text-xs text-zinc-500">
                Enter your custom domain for white-labeled access
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="logo_url" className="text-zinc-950 font-medium">Logo URL</Label>
              <Input
                id="logo_url"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                placeholder="https://yourcompany.com/logo.png"
                className="border-zinc-200 max-w-md"
                data-testid="logo-url-input"
              />
              <p className="text-xs text-zinc-500">
                URL to your company logo (recommended: 200x200px PNG)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Branding */}
        <Card className="border-zinc-200 bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-zinc-950 flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Branding
            </CardTitle>
            <CardDescription>Customize colors and visual elements</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="primary_color" className="text-zinc-950 font-medium">Primary Color</Label>
              <div className="flex items-center gap-3">
                <Input
                  id="primary_color"
                  type="color"
                  value={formData.primary_color}
                  onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                  className="w-16 h-10 p-1 border-zinc-200 cursor-pointer"
                  data-testid="primary-color-input"
                />
                <Input
                  value={formData.primary_color}
                  onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                  placeholder="#1d4ed8"
                  className="border-zinc-200 w-32"
                />
                <div 
                  className="px-4 py-2 rounded text-white text-sm font-medium"
                  style={{ backgroundColor: formData.primary_color }}
                >
                  Preview
                </div>
              </div>
              <p className="text-xs text-zinc-500">
                This color will be used for buttons and accents throughout the dashboard
              </p>
            </div>

            {/* Preview Card */}
            <div className="mt-6 p-4 bg-zinc-50 rounded-lg">
              <p className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-3">
                Preview
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-zinc-200">
                  {formData.logo_url ? (
                    <img 
                      src={formData.logo_url} 
                      alt="Logo preview" 
                      className="w-10 h-10 rounded object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div 
                      className="w-10 h-10 rounded flex items-center justify-center text-white text-lg font-bold"
                      style={{ backgroundColor: formData.primary_color }}
                    >
                      {formData.name?.charAt(0) || 'M'}
                    </div>
                  )}
                  <span className="font-semibold text-zinc-950">
                    {formData.name || 'Organization Name'}
                  </span>
                </div>
                <Button 
                  type="button"
                  style={{ backgroundColor: formData.primary_color }}
                  className="text-white hover:opacity-90"
                >
                  Sample Button
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={saving}
            className="bg-blue-700 hover:bg-blue-800 text-white"
            data-testid="save-settings-button"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Saving...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                Save Settings
              </span>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SettingsPage;
